/**
 * Dynamically derive Cloudflare Worker URLs from the current hostname.
 *
 * If running on workers.dev:
 *   space-antics-ui.parkins-xxx.workers.dev -> space-antics-ai.parkins-xxx.workers.dev
 *   space-antics-ui-staging.parkins-xxx.workers.dev -> space-antics-ai-staging.parkins-xxx.workers.dev
 *
 * If running locally:
 *   Falls back to localhost with specific ports
 */

interface WorkerUrls {
  aiService: string;
  databaseService: string;
  gameService: string;
  diceMcp: string;
  gameMcp: string;  // Game context service (R2 + lore/history)
  databaseMcp: string;
  bbcSoundMcp: string;  // BBC Micro sound effects
  elevenlabsProxy: string;  // ElevenLabs TTS/STT proxy
}

function getWorkerUrls(): WorkerUrls {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

  // Check if we're on a workers.dev domain
  // Match: worker-name.account-subdomain.workers.dev
  const workersDevMatch = hostname.match(/^([\w-]+)\.([\w-]+\.workers\.dev)$/);

  if (workersDevMatch) {
    const workerName = workersDevMatch[1]; // e.g., "space-antics-ui" or "space-antics-ui-staging"
    const accountSubdomain = workersDevMatch[2]; // e.g., "parkins-xxx.workers.dev"

    // Detect if we're on staging by checking the worker name suffix
    const isStaging = workerName.endsWith('-staging');
    const suffix = isStaging ? '-staging' : '';

    // Build worker URLs with the appropriate suffix
    const buildUrl = (name: string) => `https://${name}${suffix}.${accountSubdomain}`;

    return {
      aiService: buildUrl('space-antics-ai'),
      databaseService: buildUrl('space-antics-database'),
      gameService: buildUrl('space-antics-game'),
      diceMcp: `${buildUrl('space-antics-dice')}/mcp`,
      gameMcp: `${buildUrl('space-antics-game')}/mcp`,
      databaseMcp: `${buildUrl('space-antics-database')}/mcp`,
      bbcSoundMcp: `${buildUrl('space-antics-bbc-sound')}/mcp`,
      elevenlabsProxy: buildUrl('space-antics-elevenlabs'),
    };
  }

  // Local development - use environment variables or defaults
  return {
    aiService: import.meta.env.VITE_AI_SERVICE_URL || 'http://localhost:8792',
    databaseService: import.meta.env.VITE_DATABASE_SERVICE_URL || 'http://localhost:8795',
    gameService: import.meta.env.VITE_GAME_SERVICE_URL || 'http://localhost:8793',
    diceMcp: import.meta.env.VITE_DICE_MCP_URL || 'http://localhost:8790/mcp',
    gameMcp: import.meta.env.VITE_GAME_MCP_URL || 'http://localhost:8793/mcp',
    databaseMcp: import.meta.env.VITE_DATABASE_MCP_URL || 'http://localhost:8795/mcp',
    bbcSoundMcp: import.meta.env.VITE_BBC_SOUND_MCP_URL || 'http://localhost:8791/mcp',
    elevenlabsProxy: import.meta.env.VITE_ELEVENLABS_PROXY_URL || 'http://localhost:8794',
  };
}

// Cache the URLs since hostname won't change during runtime
let cachedUrls: WorkerUrls | null = null;

export function getUrls(): WorkerUrls {
  if (!cachedUrls) {
    cachedUrls = getWorkerUrls();

    // Log for debugging
    if (typeof window !== 'undefined') {
      console.log('[WorkerUrls] Detected environment:', {
        hostname: window.location.hostname,
        urls: cachedUrls,
      });
    }
  }
  return cachedUrls;
}

// Convenience exports
export const AI_SERVICE_URL = () => getUrls().aiService;
export const DATABASE_SERVICE_URL = () => getUrls().databaseService;
export const GAME_SERVICE_URL = () => getUrls().gameService;
export const DICE_MCP_URL = () => getUrls().diceMcp;
export const GAME_MCP_URL = () => getUrls().gameMcp;
export const DATABASE_MCP_URL = () => getUrls().databaseMcp;
export const BBC_SOUND_MCP_URL = () => getUrls().bbcSoundMcp;
export const ELEVENLABS_PROXY_URL = () => getUrls().elevenlabsProxy;
// Backwards compatibility alias
export const CONTEXT_MCP_URL = () => getUrls().gameMcp;
